import React, { useState } from 'react';
import { ArrowRight, Download, ExternalLink, FileText } from 'lucide-react';
import {
  FileLessonDetails,
  FileLessonFile,
  isPdfFile,
  resolveFileUrl,
  toEmbedPdfUrl,
} from '../../utils/lessonHelpers';

interface FileLessonContentProps {
  lessonName: string;
  lessonTitle?: string | null;
  details: FileLessonDetails;
}

const FileLessonContent: React.FC<FileLessonContentProps> = ({
  lessonName,
  lessonTitle,
  details,
}) => {
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);
  const files = details.files ?? [];

  const openFile = (file: FileLessonFile, index: number) => {
    if (isPdfFile(file)) {
      setSelectedFileIndex(index);
      return;
    }
    window.open(resolveFileUrl(file.fileUrl), '_blank', 'noopener,noreferrer');
  };

  if (files.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 text-center">
        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" aria-hidden />
        <p className="font-cairo text-slate-500">لا توجد ملفات متاحة</p>
      </div>
    );
  }

  if (selectedFileIndex !== null) {
    const selectedFile = files[selectedFileIndex];
    if (!selectedFile) return null;

    return (
      <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-emerald-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => setSelectedFileIndex(null)}
            className="inline-flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors font-cairo text-sm"
          >
            <ArrowRight className="w-5 h-5 rtl:rotate-180" aria-hidden />
            العودة إلى قائمة الملفات
          </button>
          <h3 className="font-el-messiri text-lg font-bold text-slate-900 truncate">
            {selectedFile.fileName}
          </h3>
        </div>

        <div className="border border-emerald-100 rounded-xl overflow-hidden bg-slate-50">
          <div className="w-full" style={{ height: 'min(80vh, 720px)' }}>
            <iframe
              src={toEmbedPdfUrl(selectedFile.fileUrl)}
              className="w-full h-full border-0"
              title={selectedFile.fileName}
              allow="fullscreen"
            />
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <a
            href={resolveFileUrl(selectedFile.fileUrl)}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white bg-gradient-to-r from-emerald-700 to-emerald-800 hover:brightness-105 transition font-cairo text-sm font-semibold"
          >
            <Download className="w-5 h-5" aria-hidden />
            تحميل الملف
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-emerald-100">
      <div className="mb-6">
        <h2 className="font-el-messiri text-2xl font-bold text-slate-900 mb-2">{lessonName}</h2>
        {lessonTitle && lessonTitle !== 'null' && (
          <p className="font-cairo text-slate-600">{lessonTitle}</p>
        )}
      </div>

      <h3 className="font-el-messiri text-lg font-semibold text-slate-900 mb-4">الملفات</h3>
      <div className="space-y-3">
        {files.map((file, index) => {
          const pdf = isPdfFile(file);
          return (
            <button
              key={`${file.fileName}-${index}`}
              type="button"
              onClick={() => openFile(file, index)}
              className="w-full flex items-center justify-between gap-4 p-4 bg-emerald-50/50 hover:bg-emerald-50 rounded-xl transition-colors border border-emerald-100 group text-right"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                    pdf
                      ? 'bg-gradient-to-br from-red-500/10 to-red-600/20'
                      : 'bg-gradient-to-br from-purple-500/10 to-purple-600/20'
                  }`}
                >
                  <FileText
                    className={`w-6 h-6 ${pdf ? 'text-red-600' : 'text-purple-600'}`}
                    aria-hidden
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-cairo font-medium text-slate-900 truncate">{file.fileName}</h4>
                  <p className="font-cairo text-sm text-slate-500 truncate">
                    {pdf ? 'عرض داخل المنصة' : 'فتح في نافذة جديدة'}
                  </p>
                </div>
              </div>
              {pdf ? (
                <FileText className="w-5 h-5 text-slate-400 group-hover:text-emerald-700 shrink-0" />
              ) : (
                <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-purple-600 shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FileLessonContent;
